/**
 * AUDIO — audio context, master gain + limiter chain, shared noise buffer.
 * Owned by AUDIO; see CONTRACTS.md.
 */
import {
  LIMITER_ATTACK_S,
  LIMITER_KNEE_DB,
  LIMITER_RATIO,
  LIMITER_RELEASE_S,
  LIMITER_THRESHOLD_DB,
  MASTER_GAIN,
  NOISE_BUFFER_SECONDS
} from './constants'

export interface AudioEngine {
  readonly ctx: AudioContext
  /** All layers connect here; routed through the limiter to destination. */
  readonly master: GainNode
  /** Shared looping white-noise source material. */
  readonly noiseBuffer: AudioBuffer
}

const createNoiseBuffer = (ctx: AudioContext): AudioBuffer => {
  const length = Math.floor(ctx.sampleRate * NOISE_BUFFER_SECONDS)
  const buffer = ctx.createBuffer(1, length, ctx.sampleRate)
  const data = buffer.getChannelData(0)
  for (let i = 0; i < length; i += 1) {
    data[i] = Math.random() * 2 - 1
  }
  return buffer
}

/**
 * Build the master chain: master gain → compressor-as-limiter → destination.
 * Must only be called after the title-dismissal user gesture.
 */
export const createEngine = (): AudioEngine => {
  const ctx = new AudioContext()

  const limiter = ctx.createDynamicsCompressor()
  limiter.threshold.value = LIMITER_THRESHOLD_DB
  limiter.knee.value = LIMITER_KNEE_DB
  limiter.ratio.value = LIMITER_RATIO
  limiter.attack.value = LIMITER_ATTACK_S
  limiter.release.value = LIMITER_RELEASE_S
  limiter.connect(ctx.destination)

  const master = ctx.createGain()
  master.gain.value = MASTER_GAIN
  master.connect(limiter)

  return { ctx, master, noiseBuffer: createNoiseBuffer(ctx) }
}

/** Start a looping buffer source through a filter chain into a gain node. */
export const startLoop = (
  engine: AudioEngine,
  buffer: AudioBuffer,
  nodes: readonly AudioNode[],
  initialGain: number
): GainNode => {
  const source = engine.ctx.createBufferSource()
  source.buffer = buffer
  source.loop = true

  const gain = engine.ctx.createGain()
  gain.gain.value = initialGain

  const chain: readonly AudioNode[] = [source, ...nodes, gain, engine.master]
  chain.forEach((node, i) => {
    const next = chain[i + 1]
    if (next) node.connect(next)
  })

  source.start()
  return gain
}

/** Smoothly ramp an AudioParam to a target over `seconds`. */
export const rampTo = (
  engine: AudioEngine,
  param: AudioParam,
  target: number,
  seconds: number
): void => {
  const now = engine.ctx.currentTime
  param.cancelScheduledValues(now)
  param.setValueAtTime(param.value, now)
  param.linearRampToValueAtTime(target, now + seconds)
}
